import { Geofence } from '../types/geofence';

/**
 * Client-side geofence containment for planner feedback (issue #451).
 *
 * This has to agree with `routing.Zone.Contains` on the backend, or the planner
 * would mark a waypoint legal that save then rejects. Both sides use even-odd
 * ray casting over the same vertices; the Go side first projects to metres, but
 * that projection is an affine map with positive scales on both axes, which
 * leaves every inside/outside verdict unchanged. So working in raw degrees here
 * is equivalent, not an approximation.
 */

/** Metres. Matches `gps.EarthRadiusMeters` on the backend. */
const EARTH_RADIUS_M = 6371000;

/**
 * Segments the backend approximates a circular fence with (`DefaultConfig` in
 * internal/routing). It must match: a coarser or finer polygon disagrees with
 * the server near the boundary, which is exactly where the answer matters.
 */
const CIRCLE_SEGMENTS = 16;

/** A fence vertex as Leaflet wants it: `[lat, lng]`. */
export type Vertex = [number, number];

/** Circle fence as a polygon, mirroring `routing.CircleZone` vertex for vertex. */
const circleVertices = (
  centreLat: number,
  centreLng: number,
  radiusM: number
): Vertex[] => {
  const latRad = (centreLat * Math.PI) / 180;
  return Array.from({ length: CIRCLE_SEGMENTS }, (_, i) => {
    const theta = (2 * Math.PI * i) / CIRCLE_SEGMENTS;
    const dLat =
      ((radiusM * Math.cos(theta)) / EARTH_RADIUS_M) * (180 / Math.PI);
    const dLng =
      ((radiusM * Math.sin(theta)) / (EARTH_RADIUS_M * Math.cos(latRad))) *
      (180 / Math.PI);
    return [centreLat + dLat, centreLng + dLng] as Vertex;
  });
};

/**
 * The fence's boundary as `[lat, lng]` vertices, or an empty array when the
 * geometry is unusable. Empty means "bounds no area", and every containment
 * test against it fails — the same way the backend treats a degenerate fence.
 */
export const geofenceVertices = (geofence: Geofence): Vertex[] => {
  const { type, coordinates, radius_m: radiusM } = geofence.geometry;

  if (type === 'circle') {
    const centre = coordinates as number[];
    if (centre.length < 2 || !radiusM || radiusM <= 0) {
      return [];
    }
    return circleVertices(centre[1], centre[0], radiusM);
  }

  if (type !== 'polygon' || !Array.isArray(coordinates)) {
    return [];
  }

  const ring = coordinates as number[][];
  const vertices: Vertex[] = [];
  for (const pair of ring) {
    if (!Array.isArray(pair) || pair.length < 2) {
      continue;
    }
    vertices.push([pair[1], pair[0]]); // stored lon-first
  }

  // Drop the duplicated closing vertex when the ring is explicitly closed.
  const last = vertices.length - 1;
  if (
    vertices.length >= 2 &&
    vertices[0][0] === vertices[last][0] &&
    vertices[0][1] === vertices[last][1]
  ) {
    vertices.pop();
  }

  return vertices.length >= 3 ? vertices : [];
};

/**
 * Whether `[lat, lng]` lies inside the boundary, by even-odd ray casting —
 * the same rule, in the same vertex order, as `polygon.contains` in
 * internal/routing/geometry.go.
 *
 * A point exactly on an edge has no defined answer under ray casting, on either
 * side. The planner should treat "on the line" as too close regardless.
 */
export const isInsideBoundary = (
  vertices: Vertex[],
  lat: number,
  lng: number
): boolean => {
  if (vertices.length < 3) {
    return false;
  }
  let inside = false;
  for (let i = 0, j = vertices.length - 1; i < vertices.length; j = i, i += 1) {
    const [latI, lngI] = vertices[i];
    const [latJ, lngJ] = vertices[j];
    if (latI > lat !== latJ > lat) {
      const lngCross = ((lngJ - lngI) * (lat - latI)) / (latJ - latI) + lngI;
      if (lng < lngCross) {
        inside = !inside;
      }
    }
  }
  return inside;
};

/**
 * Whether the fence constrains planning at all. Only an *active inclusion*
 * fence does: exclusion zones are routed around for path display and are not
 * enforced against a draft (issue #403).
 */
export const constrainsPlanning = (geofence: Geofence | null): boolean =>
  geofence !== null &&
  geofence.is_active &&
  geofence.inclusion_rule === 'include';

/**
 * The highest a drone may be flown, in metres (issue #541).
 *
 * EU 2019/947 puts the open-category limit at 120 m from the closest point of
 * the surface of the earth. This must equal `routing.RegulatoryCeilingM` on the
 * backend, and `ui_altitude_ceiling_test.go` fails the build if it drifts — a
 * planner that clears a height the server refuses is a planner that teaches
 * operators to ignore it.
 *
 * Note the frame: the backend measures altitude from the *launch point*, not
 * the ground beneath the aircraft, on the stated assumption that the ground
 * inside a fence is level with where it took off.
 */
export const REGULATORY_CEILING_M = 120;

/**
 * The datum every altitude here is measured from (issue #543).
 *
 * Must equal `routing.AltitudeFrame` on the backend — `ui_altitude_ceiling_test.go`
 * fails the build if the two drift, the same guard that holds the ceiling itself
 * in step (§8).
 *
 * The frame was true everywhere and said almost nowhere: the altitude-allowed
 * line named it, the violation warning beside it did not, and neither did the
 * API's refusal. "Up to 120 m" is the regulatory number with its datum removed,
 * and the datum is the whole of the difference between what this system enforces
 * and what EU 2019/947 requires. Saying it does not close #543 — a stated
 * limitation is still a limitation — but it lets the pilot who is accountable
 * for the flight apply their own judgement to terrain the system cannot see.
 */
export const ALTITUDE_FRAME = 'above the launch point';

/** The vertical slice of airspace a flight may use. */
export interface AltitudeBand {
  /** Lowest permitted height; 0 means none is set, and none is enforced. */
  floorM: number;
  /** Highest permitted height. Never 0 — absent a fence limit, the law's. */
  ceilingM: number;
}

/**
 * The band a fence confines a flight to, mirroring `routing.Band` on the
 * backend.
 *
 * A fence may tighten the regulatory ceiling and never raise it, and a fence
 * that sets no bounds — or no fence at all — still carries the regulatory one.
 * That is why this takes a nullable geofence and always returns a band.
 */
export const altitudeBand = (geofence: Geofence | null): AltitudeBand => {
  const requested = constrainsPlanning(geofence) ? geofence! : null;
  const ceiling = requested?.altitude_max ?? 0;
  const floor = requested?.altitude_min ?? 0;
  return {
    floorM: floor > 0 ? floor : 0,
    ceilingM: ceiling > 0 && ceiling <= REGULATORY_CEILING_M ? ceiling : REGULATORY_CEILING_M,
  };
};

/**
 * Whether a height, in metres above the launch point, is within the band.
 *
 * A floor of zero is the ground, which is no floor at all — enforcing it would
 * flag every waypoint the operator has not given a height yet.
 */
export const isWithinBand = (band: AltitudeBand, altitudeM: number): boolean => {
  if (altitudeM > band.ceilingM) {
    return false;
  }
  return band.floorM <= 0 || altitudeM >= band.floorM;
};

/**
 * The band as an operator needs to read it in a warning, including the datum.
 *
 * Mirrors `AltitudeBand.String()` on the backend, frame and all — a refusal from
 * the API and a warning from the planner describe the same limit, so they should
 * not describe it differently.
 */
export const describeBand = (band: AltitudeBand): string =>
  band.floorM > 0
    ? `${band.floorM}–${band.ceilingM} m ${ALTITUDE_FRAME}`
    : `up to ${band.ceilingM} m ${ALTITUDE_FRAME}`;
